import { motion } from "framer-motion";
import { Clock, PencilRuler, Shirt, Truck, Zap } from "lucide-react";
import ShopButton from "@/components/mall/ShopButton";
import { BillboardFrame, Bullets, FinePrint, Lines, MoreDetails, Panel, RoomNumber } from "@/components/mall/BillboardKit";

const GREEN = "#60A5FA";

const STOCK = [
    "100% cotton t-shirts",
    "Next Level Sueded 60/40 t-shirts",
    "Sweatshirts and hoodies",
    "Tank tops and V-necks",
    "Youth and toddler sizes",
];

const PRICES = [
    ["One shirt with one print location", "$15"],
    ["Each extra print location", "+$5"],
    ["Sueded shirts", "+$5"],
    ["Sweatshirts", "+$5"],
    ["Hoodies", "+$10"],
    ["Zip-up hoodies", "+$15"],
    ["Tank tops and youth sizes", "$15 · no extra charge"],
];

const DTF = [
    ["One DTF print", "$5 a piece"],
    ["DTF gang sheets", "$24 for the first foot, then $5 a foot"],
    ["Sheet width", '15.5" wide'],
];

const RULES = [
    ["Shirts you bring in", "They must be new, clean, unwrapped, folded and sorted by size, with no tags or stickers. We do not replace customer shirts that get damaged."],
    ["Artwork", "Print-ready files work best. If your image is low quality, we recreate it with AI for $24."],
    ["Pickup", "Our pickup address and hours are printed at the top of this page."],
    ["Delivery", "We can deliver. You pay the delivery cost plus a $10 fee."],
];

/** Room 01 — the green sign at the top of the hall, with the guy holding the white shirt. */
export default function WhiteTees() {
    return (
        <BillboardFrame testid="frame-white" room="white" note="same-day white tees">
        <section className="dark-block relative overflow-hidden" data-testid="mall-room-white">
            <div className="absolute inset-0">
                <img src="/assets/storefront.png" alt="" className="h-full w-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/90 to-brand-ink/40" />
            </div>
            <div className="pointer-events-none absolute -left-24 top-10 h-80 w-80 rounded-full blur-[140px]" style={{ background: "rgba(37, 99, 235,0.22)" }} />

            <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-10 sm:px-6 md:py-12 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <div className="flex flex-wrap items-center gap-3">
                        <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-widest"
                            style={{ borderColor: "rgba(37, 99, 235,0.4)", background: "rgba(37, 99, 235,0.1)", color: GREEN }}>
                            <span className="h-2 w-2 animate-pulse rounded-full" style={{ background: GREEN }} /> We are printing today. Order by 2 PM.
                        </motion.span>
                    </div>
                    <RoomNumber n="01" room="Shirts · Same Day" accent={GREEN} dark className="mt-6" />
                    <motion.h2 initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.6 }} data-testid="white-headline"
                        className="mt-4 font-display text-4xl font-black leading-[0.92] tracking-tighter text-white sm:text-5xl lg:text-6xl">
                        WE PRINT WHITE SHIRTS<br /><span className="text-glow-green" style={{ color: GREEN }}>THE SAME DAY YOU ORDER.</span>
                    </motion.h2>
                    <p className="mt-6 max-w-xl text-base text-zinc-300 md:text-lg" data-testid="white-body">
                        White shirts are always in stock, so we print them the day you order. Order and pay before
                        <span className="font-bold text-white"> 2:00 PM</span> and your shirts are ready before midnight tonight.
                        Order after 2:00 PM and they are ready before midnight tomorrow. One shirt costs
                        <span className="font-bold text-white"> $15</span>.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <ShopButton room="white" tone="green" testid="room-white-cta"
                            title="Which shop prints your white t-shirts?"
                            path={(s) => `/shirts/white?store=${s}`}>
                            <Zap size={17} /> Order white t-shirts now
                        </ShopButton>
                        <ShopButton room="apparel" tone="outline" testid="room-white-browse"
                            title="Which shop's apparel do you want to see?" path={(s) => `/shirts?store=${s}`}>
                            <Shirt size={16} /> See all apparel
                        </ShopButton>
                    </div>
                    <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500">
                        The prices on this page are the standard catalog prices. Our exact price shows in the designer before you pay.
                    </p>
                </div>

                <div className="relative lg:col-span-5">
                    <motion.div initial={{ opacity: 0, scale: 0.92, rotate: -3 }} whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                        viewport={{ once: true }} transition={{ duration: 0.8 }} className="relative">
                        <div className="absolute -inset-4 rounded-[2rem] blur-3xl" style={{ background: "rgba(37, 99, 235,0.2)" }} />
                        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 glow-green">
                            <img src="/assets/mascot.png" alt="Holding a fresh white tee" data-testid="hero-mascot"
                                className="mx-auto max-h-[300px] w-full object-cover" />
                        </div>
                    </motion.div>
                </div>
            </div>

            <MoreDetails dark testid="white-details" className="mx-auto max-w-7xl px-4 pb-10 sm:px-6"
                label="What is in stock, how same-day works, prices and the fine print">
                <div className="grid gap-5 lg:grid-cols-3">
                    <Panel icon={Shirt} title="These are in stock and print today" accent={GREEN} dark testid="white-stock">
                        <Bullets items={STOCK} accent={GREEN} dark />
                    </Panel>
                    <Panel icon={Clock} title="How same day works" accent={GREEN} dark testid="white-sameday">
                        <div className="space-y-4">
                            {[["Order before 2:00 PM.", "Your shirts are ready before midnight tonight."],
                            ["Order after 2:00 PM.", "Your shirts are ready before midnight tomorrow."]].map(([a, b]) => (
                                <div key={a} className="rounded-2xl border p-4" style={{ borderColor: "rgba(37, 99, 235,0.28)", background: "rgba(37, 99, 235,0.06)" }}>
                                    <p className="font-display text-lg font-bold" style={{ color: GREEN }}>{a}</p>
                                    <p className="mt-1 text-sm text-zinc-300">{b}</p>
                                </div>
                            ))}
                        </div>
                    </Panel>
                    <Panel icon={Truck} title="What it costs" accent={GREEN} dark testid="white-pricing">
                        <Lines rows={PRICES} accent="#ffffff" dark />
                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em]" style={{ color: GREEN }}>DTF prints</p>
                        <Lines rows={DTF} accent="#ffffff" dark className="mt-1" />
                        <p className="mt-3 text-xs text-zinc-400">A gang sheet is the cheapest way to print many pieces at once.</p>
                    </Panel>
                </div>
                <FinePrint items={RULES} dark className="mt-10" testid="white-fineprint" />
                <p className="mt-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: GREEN }}>
                    <PencilRuler size={12} /> Most orders placed before 2 PM are ready the same day.
                </p>
            </MoreDetails>
        </section>
        </BillboardFrame>
    );
}
